import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { ContestRole, SystemRole } from '../../models/User';
import { AuthService } from '../services/auth.service';
import { ContextService } from '../services/context.service';
import { UserService } from '../services/user.service';

export const contestRedirectGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  if (!isBrowser) {
    return true;
  }

  const router = inject(Router);
  const authService = inject(AuthService);
  const contextService = inject(ContextService);
  const userService = inject(UserService);

  const contestId = route.params['contestId'] as string | undefined;
  if (!contestId || route.firstChild) {
    return true;
  }

  const systemRole = authService.getSystemRole();
  if (systemRole === SystemRole.SuperAdmin || systemRole === SystemRole.Admin) {
    return router.createUrlTree(['/contests', contestId, 'dashboard']);
  }

  const isConnected = authService.isConnected();
  if (!isConnected) {
    return true;
  }

  const userId = authService.getUserId();
  if (!userId) {
    return router.createUrlTree(['/contests', contestId, 'run']);
  }

  return userService.getUserParticipants(userId).pipe(
    map((participants) => {
      console.error(`[GUARD] Participants for ${contestId}:`, JSON.stringify(participants));
      contextService.updateParticipants(participants);
      const p = participants.find(
        (p) => String(p.contestId).toLowerCase() === String(contestId).toLowerCase(),
      );
      console.error(`[GUARD] Found participant:`, JSON.stringify(p));
      if (p?.contestRole === ContestRole.Organizer) {
        return router.createUrlTree(['/contests', contestId, 'dashboard']);
      }
      return router.createUrlTree(['/contests', contestId, 'run']);
    }),
    catchError((err) => {
      console.error(`[GUARD] Error in contestRedirectGuard:`, err);
      return of(router.createUrlTree(['/contests', contestId, 'run']));
    }),
  );
};
